import { useEffect, useState, useCallback } from 'react';
import { websocketClient } from '../services/websocket-client';
import { getDisplayContent } from '../lib/notification-display';
import { env } from '@/lib/env';

interface NotificationPayload {
  id: string;
  type: string;
  title: string;
  message: string;
  shortformTitle?: string;
  shortformMessage?: string;
  relatedHoldRequestId?: string;
  relatedLineItemId?: string;
  isRead?: boolean;
  createdAt: string;
}

/**
 * Enrich a notification payload with generated shortform fields if they are missing.
 * For legacy notifications (those without shortform_title/shortform_message),
 * this generates shortform on-the-fly from longform content using greeting removal,
 * URL removal, and truncation.
 *
 * Exported as `enrichNotification` for use by components (e.g. NotificationBell)
 * that receive notifications via WebSocket and need consistent display with
 * REST API responses.
 *
 * Validates: Requirements 5.1, 5.2, 5.4
 */
export function enrichLegacyNotification<T extends { id?: string; title: string; message: string; shortformTitle?: string; shortformMessage?: string }>(notification: T): T {
  if (notification.shortformTitle && notification.shortformMessage) {
    return notification;
  }
  const display = getDisplayContent(notification);
  return {
    ...notification,
    shortformTitle: notification.shortformTitle || display.title,
    shortformMessage: notification.shortformMessage || display.message,
  };
}

interface StatusUpdatePayload {
  entityType: 'hold_request' | 'line_item';
  entityId: string;
  oldStatus: string;
  newStatus: string;
  updatedBy: string;
  updatedAt: Date;
}

/**
 * Hook for WebSocket connection management
 */
export function useWebSocket(token: string | null) {
  const [isConnected, setIsConnected] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    // In Entra mode, token is null — EntraAuthContext owns the connection lifecycle.
    // In mock mode, token is the HS256 JWT and this hook initiates the connection.
    if (token && typeof token === 'string' && token.length > 0) {
      websocketClient.connect(token, env.API_URL);
    }

    // Always register listeners regardless of who initiated the connection,
    // so isConnected and unreadCount reflect the actual socket state.
    const unsubscribeConnection = websocketClient.onConnectionChange((connected) => {
      setIsConnected(connected);
      // When the socket connects (or reconnects), request the current unread
      // count. The server sends notifications:unread_count on connect, but if
      // this listener was registered after the socket was already connecting
      // (not yet connected), the initial event was missed. This also handles
      // the reconnect case where we need fresh state.
      if (connected) {
        websocketClient.refreshUnreadCount();
      }
    });

    const unsubscribeUnreadCount = websocketClient.onUnreadCount((count) => {
      setUnreadCount(count);
    });

    // Seed initial state in case the socket is already up before this hook mounted
    // (EntraAuthContext may have connected during auth restore before Layout rendered).
    // If already connected, also request a fresh unread count since the server's
    // initial push on connect was missed before listeners were registered.
    if (websocketClient.isConnected()) {
      setIsConnected(true);
      websocketClient.refreshUnreadCount();
    }

    return () => {
      unsubscribeConnection();
      unsubscribeUnreadCount();
      // Do not disconnect here — the WebSocket is a singleton owned by the auth
      // context (AuthContext / EntraAuthContext), which disconnects on logout.
      // Disconnecting on hook unmount would tear down the shared connection for
      // all other mounted consumers (e.g. bell + Notifications page).
    };
  }, [token]);

  return { isConnected, unreadCount };
}

/**
 * Hook for listening to notifications
 */
export function useNotifications(
  onNotification: (notification: NotificationPayload) => void
) {
  useEffect(() => {
    const unsubscribe = websocketClient.onNotification(onNotification);
    return unsubscribe;
  }, [onNotification]);
}

/**
 * Hook for subscribing to hold request updates
 */
export function useHoldRequestUpdates(
  holdRequestId: string | null,
  onUpdate: (update: StatusUpdatePayload) => void
) {
  useEffect(() => {
    if (!holdRequestId) return;

    // Subscribe to updates
    websocketClient.subscribeToHoldRequest(holdRequestId);

    // Listen for updates
    const unsubscribe = websocketClient.onStatusUpdate(
      `hold_request:${holdRequestId}`,
      onUpdate
    );

    // Cleanup
    return () => {
      unsubscribe();
      websocketClient.unsubscribeFromHoldRequest(holdRequestId);
    };
  }, [holdRequestId, onUpdate]);
}

/**
 * Hook for subscribing to line item updates
 */
export function useLineItemUpdates(
  lineItemId: string | null,
  onUpdate: (update: StatusUpdatePayload) => void
) {
  useEffect(() => {
    if (!lineItemId) return;

    // Subscribe to updates
    websocketClient.subscribeToLineItem(lineItemId);

    // Listen for updates
    const unsubscribe = websocketClient.onStatusUpdate(
      `line_item:${lineItemId}`,
      onUpdate
    );

    // Cleanup
    return () => {
      unsubscribe();
      websocketClient.unsubscribeFromLineItem(lineItemId);
    };
  }, [lineItemId, onUpdate]);
}

/**
 * Hook for notification actions
 */
export function useNotificationActions() {
  const markAsRead = useCallback((notificationId: string) => {
    websocketClient.markNotificationAsRead(notificationId);
  }, []);

  const getNotifications = useCallback((limit: number = 20, offset: number = 0) => {
    websocketClient.getNotifications(limit, offset);
  }, []);

  return {
    markAsRead,
    getNotifications,
  };
}

/**
 * Hook for fetching notifications from REST API
 */
export function useNotificationsAPI(token: string | null) {
  const [notifications, setNotifications] = useState<NotificationPayload[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // In Entra mode the token prop is null (EntraAuthContext doesn't expose a
  // token field via the auth context interface). Fall back to localStorage
  // where the ID token is stored on login — same approach as api-client.ts.
  const getAuthToken = useCallback((): string | null => {
    return token || localStorage.getItem('auth_token');
  }, [token]);

  const fetchNotifications = useCallback(async (limit: number = 20, offset: number = 0, unreadOnly: boolean = true) => {
    const authToken = getAuthToken();
    if (!authToken) {
      setError('No authentication token available');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
        unreadOnly: unreadOnly.toString(),
      });

      const response = await fetch(`${env.API_BASE_URL}/notifications?${params}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch notifications: ${response.status}`);
      }

      const data = await response.json();
      // Enrich legacy notifications (missing shortform fields) with generated shortform
      // Validates: Requirements 5.1, 5.2, 5.4
      const enriched = (data.notifications || []).map(enrichLegacyNotification);
      setNotifications(enriched);
      setTotal(data.total ?? enriched.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch notifications');
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthToken]);

  const markAsReadAPI = useCallback(async (notificationId: string) => {
    const authToken = getAuthToken();
    if (!authToken) {
      console.error('No authentication token available');
      return;
    }

    try {
      const response = await fetch(`${env.API_BASE_URL}/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to mark notification as read: ${response.status}`);
      }

      // Remove from local state
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      
      // Notify WebSocket to mark as read and refresh unread count
      websocketClient.markNotificationAsRead(notificationId);
      websocketClient.refreshUnreadCount();
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  }, [getAuthToken]);

  const markAllAsReadAPI = useCallback(async () => {
    const authToken = getAuthToken();
    if (!authToken) {
      console.error('No authentication token available');
      return;
    }

    try {
      const response = await fetch(`${env.API_BASE_URL}/notifications/read-all`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to mark all notifications as read: ${response.status}`);
      }

      // Clear local state
      setNotifications([]);
      
      // Request updated unread count from WebSocket
      websocketClient.refreshUnreadCount();
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  }, [getAuthToken]);

  return {
    notifications,
    total,
    loading,
    error,
    fetchNotifications,
    markAsRead: markAsReadAPI,
    markAllAsRead: markAllAsReadAPI,
    setNotifications,
  };
}
